import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { cn } from "@portal/components/lib/utils";
import { Icon, List, Text } from "@portal/components/primitives";
import type { FC } from "react";

type AdminReviewReasonListProps = {
	reviewReasons: { description: string }[];
	className?: string;
	testId?: string;
};

/**
 * Collapsible warning card listing the reasons an account or request was flagged for manual
 * review. Sized and toned to sit alongside the overview tab's metric cards and processing
 * banner rather than float as bare text. Renders nothing when there is nothing to review.
 */
export const AdminReviewReasonList: FC<AdminReviewReasonListProps> = ({
	reviewReasons,
	className,
	testId = "admin-review-reason-list",
}) => {
	const descriptions = Array.from(new Set(reviewReasons.map((reason) => reason.description)));

	if (descriptions.length === 0) return null;

	return (
		<Disclosure>
			{({ open }) => (
				<div
					data-testid={testId}
					className={cn(
						"w-full rounded-lg border border-intent-warning-text/50 bg-intent-warning-bg overflow-hidden",
						className,
					)}
				>
					<DisclosureButton
						data-testid={`${testId}-toggle`}
						className="w-full flex items-center gap-x-[10px] px-[14px] py-[10px] text-left cursor-pointer"
					>
						<Icon type="exclamation-triangle" size="small" color="warning" />
						<Text color="primary" type="body-strong">
							{descriptions.length} Review Reason{descriptions.length === 1 ? "" : "s"}
						</Text>
						<Icon
							type="chevron-right"
							size="mini"
							color="tertiary"
							className={cn("ml-auto transition-transform duration-150", open && "rotate-90")}
						/>
					</DisclosureButton>

					<DisclosurePanel>
						<List className="space-y-[8px] border-t border-intent-warning-text/50 px-[14px] py-[10px]">
							{descriptions.map((description) => (
								<List.Item
									key={description}
									className="items-start text-[14px] leading-[20px] text-primary whitespace-pre-wrap"
									leading={
										<span className="mt-[9px] h-[4px] w-[4px] rounded-full bg-intent-warning-text" />
									}
								>
									{description}
								</List.Item>
							))}
						</List>
					</DisclosurePanel>
				</div>
			)}
		</Disclosure>
	);
};
